import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { ArrowLeft, CheckCircle, PlayCircle, Headphones, Scale, BookOpen, Keyboard, Lock } from "lucide-react";

const MODULE_INFO = {
  dictation: { label: "Dictation", icon: Headphones, path: "dictation" },
  legal: { label: "Legal", icon: Scale, path: "legal" },
  pitman: { label: "Pitman", icon: BookOpen, path: "pitman" },
  typing: { label: "Typing", icon: Keyboard, path: "typing" },
};

export default function PlaylistDetail() {
  const { id } = useParams();
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/playlists/${id}`)
      .then((r) => setPl(r.data))
      .catch(() => setPl(null))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-8">
        <Link to="/playlists" className="text-xs uppercase tracking-[0.3em] font-bold text-muted-foreground hover:text-brand-gold" data-testid="playlist-back">
          <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Back to Playlists
        </Link>

        {loading ? (
          <div className="mt-8 font-mono text-muted-foreground">Loading…</div>
        ) : !pl ? (
          <div className="mt-8 font-mono text-muted-foreground" data-testid="playlist-not-found">Playlist not found.</div>
        ) : (
          <>
            <div className="mt-6 mb-8">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-3">Guided program</div>
              <h1 className="text-4xl font-black tracking-tight" data-testid="playlist-detail-title">{pl.name}</h1>
              {pl.description && (
                <p className="mt-3 font-mono text-muted-foreground max-w-2xl">{pl.description}</p>
              )}
              <div className="mt-6 flex items-center gap-6">
                <div>
                  <div className="text-3xl font-black tracking-tight" data-testid="playlist-progress-pct">
                    {pl.items.length > 0 ? Math.round((pl.completed_count / pl.items.length) * 100) : 0}%
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Complete</div>
                </div>
                <div>
                  <div className="text-3xl font-black tracking-tight">{pl.completed_count}/{pl.items.length}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Exercises</div>
                </div>
              </div>
            </div>

            <div className="border border-border divide-y divide-black/10" data-testid="playlist-items">
              {pl.items.length === 0 && (
                <div className="p-8 text-center font-mono text-sm text-muted-foreground">This playlist is empty.</div>
              )}
              {pl.items.map((it, i) => {
                const info = MODULE_INFO[it.module] || { label: it.module, icon: PlayCircle, path: it.module };
                const Icon = info.icon;
                const to = it.missing ? "#" : `/${info.path}/${it.item_id}`;
                return (
                  <div key={`${it.module}-${it.item_id}-${i}`} className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 border-2 border-brand-gold flex items-center justify-center shrink-0">
                      <span className="text-sm font-black">{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-1">
                        <Icon className="w-3 h-3" /> {info.label}
                      </div>
                      <div className="font-bold truncate" data-testid={`playlist-item-title-${i}`}>
                        {it.missing ? "(Removed by admin)" : (it.title || "Untitled")}
                      </div>
                      {it.best_accuracy != null && (
                        <div className="text-xs font-mono text-muted-foreground mt-0.5">
                          Best: {it.best_accuracy}% · {it.best_wpm} WPM
                        </div>
                      )}
                    </div>
                    <div className="shrink-0">
                      {it.completed ? (
                        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1" data-testid={`playlist-item-done-${i}`}>
                          <CheckCircle className="w-3.5 h-3.5" /> Done
                        </span>
                      ) : it.missing ? (
                        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-muted-foreground/70 bg-secondary/40 border border-neutral-200 px-2.5 py-1">
                          <Lock className="w-3.5 h-3.5" /> Unavailable
                        </span>
                      ) : (
                        <Link
                          to={to}
                          data-testid={`playlist-item-start-${i}`}
                          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-white bg-brand-gold hover:bg-brand-gold/90 px-3 py-1.5"
                        >
                          <PlayCircle className="w-3.5 h-3.5" /> Start
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
